import { useEffect, useState } from "react";
import { useRobot } from "@/context/RobotContext";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Pulse, Thermometer, Compass, BatteryHigh, Ruler, SlidersHorizontal, WarningCircle } from "@phosphor-icons/react";

const META = {
  dist: { label: "Distance", unit: "cm", color: "#FF5A00", icon: Ruler },
  distance: { label: "Distance", unit: "cm", color: "#FF5A00", icon: Ruler },
  temp: { label: "Temperature", unit: "°C", color: "#FFB000", icon: Thermometer },
  temperature: { label: "Temperature", unit: "°C", color: "#FFB000", icon: Thermometer },
  batt: { label: "Battery", unit: "%", color: "#00E676", icon: BatteryHigh },
  battery: { label: "Battery", unit: "%", color: "#00E676", icon: BatteryHigh },
  heading: { label: "Heading", unit: "°", color: "#7C9CFF", icon: Compass },
};

const CONFIGURABLE = ["dist", "temp", "batt", "heading"];

const metaFor = (k) => META[k] || { label: k.toUpperCase(), unit: "", color: "#94A3B8", icon: Pulse };

function MetricCard({ metricKey, value, history, breach }) {
  const meta = metaFor(metricKey);
  const Icon = meta.icon;
  const data = history.filter((h) => typeof h[metricKey] === "number").map((h) => ({ v: h[metricKey] }));
  const display = typeof value === "number" ? (Number.isInteger(value) ? value : value.toFixed(1)) : value;
  const color = breach ? "#FF3B30" : meta.color;

  return (
    <div
      data-testid={`telemetry-metric-${metricKey}`}
      className={`border bg-[#0A0B0E] p-3 transition-colors ${breach ? "border-[#FF3B30] rec-dot" : "border-[#272A35]"}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#94A3B8]">
          <Icon size={13} weight="bold" style={{ color }} />
          {meta.label}
        </div>
        {breach && (
          <span className="flex items-center gap-1 text-[9px] uppercase font-bold text-[#FF3B30]" data-testid={`telemetry-alert-${metricKey}`}>
            <WarningCircle size={12} weight="fill" /> {breach.type === "min" ? "LOW" : "HIGH"}
          </span>
        )}
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-2xl font-head font-black" style={{ color }}>
          {display}
        </span>
        <span className="text-[10px] text-[#475569]">{meta.unit}</span>
      </div>
      <div className="h-8 mt-1 -mx-1">
        {data.length > 1 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={`g-${metricKey}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} fill={`url(#g-${metricKey})`} isAnimationActive={false} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full grid place-items-center text-[9px] text-[#475569]">collecting…</div>
        )}
      </div>
    </div>
  );
}

function ThresholdDialog({ open, onOpenChange, thresholds, telemetryKeys, onSave }) {
  const [draft, setDraft] = useState({});

  useEffect(() => {
    if (open) setDraft(JSON.parse(JSON.stringify(thresholds || {})));
  }, [open, thresholds]);

  const rows = Array.from(new Set([...CONFIGURABLE, ...telemetryKeys]));
  const setField = (metric, bound, val) =>
    setDraft((d) => ({ ...d, [metric]: { ...d[metric], [bound]: val === "" ? null : Number(val) } }));

  const save = () => {
    const cleaned = {};
    for (const [k, v] of Object.entries(draft)) {
      const min = v?.min;
      const max = v?.max;
      if ((min ?? null) !== null || (max ?? null) !== null) cleaned[k] = { min: min ?? null, max: max ?? null };
    }
    onSave(cleaned);
    toast.success("Alert thresholds saved");
    onOpenChange(false);
  };

  const inputCls = "w-full bg-[#0A0B0E] border border-[#272A35] px-2 py-1.5 text-sm font-mono focus:outline-none focus:border-[#FF5A00]";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111318] border border-[#272A35] rounded-none text-[#F8FAFC] font-mono max-w-md" data-testid="threshold-dialog">
        <DialogHeader>
          <DialogTitle className="font-head uppercase tracking-tight">Alert Thresholds</DialogTitle>
          <DialogDescription className="text-[#94A3B8] text-xs">
            Trigger a red alert + log entry when a reading crosses these bounds. Leave blank to ignore.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-[1fr_auto_auto] gap-2 text-[10px] uppercase tracking-widest text-[#475569]">
            <span>Metric</span>
            <span className="w-20 text-center">Min</span>
            <span className="w-20 text-center">Max</span>
          </div>
          {rows.map((k) => (
            <div key={k} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
              <span className="text-sm">{metaFor(k).label}</span>
              <input
                data-testid={`threshold-${k}-min`}
                type="number"
                value={draft[k]?.min ?? ""}
                onChange={(e) => setField(k, "min", e.target.value)}
                className={`${inputCls} w-20`}
                placeholder="—"
              />
              <input
                data-testid={`threshold-${k}-max`}
                type="number"
                value={draft[k]?.max ?? ""}
                onChange={(e) => setField(k, "max", e.target.value)}
                className={`${inputCls} w-20`}
                placeholder="—"
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 text-xs uppercase tracking-widest border border-[#272A35] hover:bg-[#1A1D24]">
            Cancel
          </button>
          <button
            data-testid="save-thresholds-btn"
            onClick={save}
            className="px-4 py-2 text-xs uppercase tracking-widest font-bold bg-[#FF5A00] text-[#0A0B0E] border border-[#FF5A00] hover:bg-transparent hover:text-[#FF5A00] transition-colors active:scale-95"
          >
            Save Thresholds
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Telemetry() {
  const {
    telemetry,
    telemetryHistory,
    simulating,
    startSimulation,
    stopSimulation,
    status,
    thresholds,
    breaches,
    loadThresholds,
    saveThresholds,
  } = useRobot();
  const [cfgOpen, setCfgOpen] = useState(false);
  const keys = Object.keys(telemetry);
  const alertCount = Object.keys(breaches).length;

  useEffect(() => {
    loadThresholds();
  }, [loadThresholds]);

  return (
    <div className="border border-[#272A35] bg-[#111318] h-full flex flex-col" data-testid="telemetry-panel">
      <div className="border-b border-[#272A35] px-4 py-3 flex items-center justify-between">
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#94A3B8] flex items-center gap-2">
          <Pulse size={16} weight="bold" className="text-[#FF5A00]" /> Live Telemetry
          {keys.length > 0 && alertCount === 0 && (
            <span className="flex items-center gap-1 text-[#00E676]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] rec-dot" /> streaming
            </span>
          )}
          {alertCount > 0 && (
            <span className="flex items-center gap-1 text-[#FF3B30]" data-testid="telemetry-alert-count">
              <WarningCircle size={13} weight="fill" /> {alertCount} alert{alertCount > 1 ? "s" : ""}
            </span>
          )}
        </span>
        <div className="flex items-center gap-2">
          <button
            data-testid="telemetry-config-btn"
            onClick={() => setCfgOpen(true)}
            className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border border-[#272A35] text-[#94A3B8] hover:border-[#FF5A00] hover:text-[#FF5A00] transition-colors active:scale-95"
          >
            <SlidersHorizontal size={13} weight="bold" /> Alerts
          </button>
          <button
            data-testid="telemetry-simulate-btn"
            onClick={() => (simulating ? stopSimulation() : startSimulation())}
            className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border transition-colors active:scale-95 ${
              simulating
                ? "border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-[#0A0B0E]"
                : "border-[#272A35] text-[#94A3B8] hover:border-[#FF5A00] hover:text-[#FF5A00]"
            }`}
            title="Generate demo sensor data without hardware"
          >
            {simulating ? "Stop Sim" : "Simulate Feed"}
          </button>
        </div>
      </div>

      <div className="p-4 flex-1">
        {keys.length === 0 ? (
          <div className="h-full grid place-items-center text-center py-6">
            <div>
              <Pulse size={34} weight="bold" className="text-[#272A35] mx-auto mb-3" />
              <p className="text-sm text-[#94A3B8]">No sensor data yet.</p>
              <p className="text-xs text-[#475569] max-w-xs mx-auto mt-1">
                {status === "connected"
                  ? "Waiting for the robot to push telemetry over its BLE notify characteristic."
                  : "Connect a robot that streams telemetry, or hit “Simulate Feed” to preview."}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="telemetry-grid">
            {keys.map((k) => (
              <MetricCard key={k} metricKey={k} value={telemetry[k].value} history={telemetryHistory} breach={breaches[k]} />
            ))}
          </div>
        )}
      </div>

      <ThresholdDialog
        open={cfgOpen}
        onOpenChange={setCfgOpen}
        thresholds={thresholds}
        telemetryKeys={keys}
        onSave={saveThresholds}
      />
    </div>
  );
}
